import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import moment from 'moment';
import { CreateUserRequest, CustomerProfile, SubmitRetroCreditRequest } from '@/models';
import { AccountCreatorArgs } from '@/components/account/CmgCreateAccountForm/cmg-create-account-form';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { EnrollmentOptions } from '@/services/loyalty/loyalty.service';
import { formatPhoneNumber, getStartOfYear, convertLocalToUTC, isLoyaltyEnabled, parseCountryCode } from '@/helpers';
import { countries } from '@/constants';
import { OrderAndHistoryMetricsRequest } from '@/models/sustainability';
import { CustomerErrorCode } from '@/services/customer/customer.service';
import { GuestInfoForm } from '@/store/checkout';

@Component({
  name: 'CmgOrderConfirmationCreateAccount',
})
export default class CmgOrderConfirmationCreateAccount extends Vue {
  currentCountry = this.$appStore.state.ui.countryProfile.countryCode;
  showForm = true; // a flag to toggle animation when swapping country

  get guest(): GuestInfoForm {
    return this.$appStore.state.checkout.guest.info;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get legalCopy(): any {
    if(window._cmgIsAEMCS){
      console.log('on AEMCS, grab legal from GQL')
      return this.$appStore.state.cms.legalCopy ? this.$appStore.state.cms.legalCopy : '';
    }
    console.log('not on AEMCS, grab legal from CF')
    return this.$appStore.state.cms.legalCopy ? this.$appStore.state.cms.legalCopy.createAccountOrderConfirmation[parseCountryCode(this.currentCountry)] : '';
  }

  get modalImageSrc(): string {
    if (this.$appStore.state.adobeTarget.orderConfirmationCreateAccountExperience) {
      return this.$appStore.state.adobeTarget.orderConfirmationCreateAccountExperience.medallionImageUrl;
    }

    let src = '';

    if (this.isLoyaltyEnabled) {
      src = this.$appStore.state.cms.images.logos.medallionRewardShout.default.replace('dev', 'int');
    } else {
      src = this.$appStore.state.cms.images.icons.pepperGeneral.default;
    }

    return src;
  }

  get prefilledRequest(): CreateUserRequest {
    const profile = this.$appStore.state.customer.profile;
    const lastOrder = this.$appStore.state.order.lastOrder.order && this.$appStore.state.order.lastOrder.order.order.delivery;

    return {
      firstName: (profile && profile.firstName) || '',
      emailAddress: (profile && profile.email) || '',
      phoneNumber: (lastOrder && lastOrder.dropOffContactPhoneNumber && formatPhoneNumber(lastOrder.dropOffContactPhoneNumber)) || (profile && profile.phoneNumber) || '',
    };
  }

  get signInContainerTitle(): string {
    if (this.currentCountry) {
      return countries[this.currentCountry.toLowerCase()].strings.signInContainerTitle;
    }

    return this.$appStore.state.ui.countryProfile.strings.signInContainerTitle;
  }

  get headerText(): string {
    if (this.$appStore.state.adobeTarget.orderConfirmationCreateAccountExperience) {
      return this.$appStore.state.adobeTarget.orderConfirmationCreateAccountExperience.header;
    }

    return this.$appStore.state.cms.orderConfirmationCreateAccountExperience?.header ?? this.$t('pages.orderConfirmation.createAccountSection.header') as string;
  }

  get subheaderText(): string {
    if (this.$appStore.state.adobeTarget.orderConfirmationCreateAccountExperience) {
      return this.$appStore.state.adobeTarget.orderConfirmationCreateAccountExperience.description;
    }

    return this.$appStore.state.cms.orderConfirmationCreateAccountExperience?.description ?? this.$t('pages.orderConfirmation.createAccountSection.subHeader') as string;
  }

  get isLoyaltyEnabled(): boolean {
    return isLoyaltyEnabled(this.currentCountry);    
  }

  get isEnrolledInLoyalty(): boolean {
    return this.$services.loyalty.isEnrolledInLoyalty(this.$appStore.state.customer.profile);
  } 
   
  async onCreateAccount(args: AccountCreatorArgs): Promise<void> {
    const request = args.request;

    const upgradeResponse = await this.$services.customer.upgradeGuestUser({
      firstName: request.firstName,
      lastName: request.lastName,
      emailAddress: request.emailAddress,
      password: request.password,
      phoneNumber: request.phoneNumber,
      country: args.country,
    });
    
    // Pause account creation until after 2SV is complete
    if (upgradeResponse == CustomerErrorCode.TwoStepVerificationRequired || upgradeResponse == CustomerErrorCode.AccountVerificationRequired) {
      this.$services.ui.setModalVisibility('verifyMobileNumber', true, {
        email: request.emailAddress || '',
        phoneNumber: request.phoneNumber || '',
        requiresPasswordVerification: false,
        requiresSignOutOnFailure: true,
        onSuccess: () => this.proceedAfterAccountCreation(args),
        onFailure: () => this.$services.ui.setHasFailed2SV(true),
      });
      Vue.nextTick(() => {
        this.$services.ui.focusDiv('.cmg-verify-mobile-number-modal');
      });
    } else {
      this.proceedAfterAccountCreation(args);
    }    
  }

  async proceedAfterAccountCreation(args: AccountCreatorArgs): Promise<void> {
    const request = args.request;
    const order = this.$appStore.state.order.lastOrder.order;

    await this.$services.customer.updateCustomer({
      firstName: request.firstName,
      lastName: request.lastName,
      email: request.emailAddress,
      phoneNumber: request.phoneNumber,
      country: args.country,
      isGuest: false,
      dateOfBirth: args.dateOfBirth
    });

    
    if (args.request.enrollInLoyalty) {
      const enrollmentOptions: EnrollmentOptions = {
         customer: this.customer, 
         pageTitle: document.title,
         shouldSubscribeToMarketingEmail: args.request.enrollInEmailMarketing,
         shouldSubscribeToMarketingSMS: args.request.enrollInSmsMarketing
      };
      await this.$services.loyalty.enrollInLoyalty(enrollmentOptions);
      this.$services.ui.showOnboardingModal(false, this.$services.customer, this.customer ?? undefined);
      this.$services.customer.setJoinedLoyaltyOnOrderConfirmationPage(true);

      if (!order) {
        return this.$services.logger.unreachable('Attempted to upgrade account from order confirmation page without an order');
      }

      const orderId = order.order.orderId;
      const meals = order.order.meals || [];
      const orderSubmitDateTimeUtc = convertLocalToUTC(order.order.orderDateTimeLocal) || moment().utc().format(); // prettier-ignore

      const retroCreditRequest: SubmitRetroCreditRequest = {
        restaurantId: order.order.restaurantId,
        checkNumber: this.$services.utils.generateRandomNumberString(5), // Need to send dummy 5 digit check number - ok because onlineOrderId is populated
        transactionDate: order.order.orderDateTimeLocal || moment().format(),
        transactionAmount: order.order.orderTotalAmount.toString(),
        onlineOrderId: order.order.orderId
      };
      this.$services.retroCredit.submit(retroCreditRequest);

      // Keep sustainability metrics
      const postSustainabilityMetricsBody: OrderAndHistoryMetricsRequest = {
        orderId,
        isGuest: false,
        country: args.country,
        meals,
        startDate: getStartOfYear(),
        orderSubmitDateTimeUtc,
      };
      await this.$services.sustainability.postCurrentOrderAndHistorySustainabilityMetrics(postSustainabilityMetricsBody);

      this.$services.ui.setLastSelectedElement('points-on-the-way');
      this.$services.ui.firstFocus();
    }
      
    await this.$services.customer.refreshCustomerProfileInfo();
  }

  onClickSignIn(event: any): void {
    if (event.target.className) {
      this.$services.ui.setLastSelectedElement(event.target.className);
    }

    this.$services.ui.setModalVisibility('signIn', true, {
      onClose: () => this.proceedAfterSignIn()
    });
    this.$services.ui.firstFocus();
  }

  async proceedAfterSignIn() {
    await this.$services.customer.refreshCustomerProfileInfo();

    if(this.isEnrolledInLoyalty) {
      const order = this.$store.state.order.lastOrder.order.order;
      
      const retroCreditRequest: SubmitRetroCreditRequest = {
        restaurantId: order.restaurantId,
        checkNumber: this.$services.utils.generateRandomNumberString(5), // Need to send dummy 5 digit check number - ok because onlineOrderId is populated
        transactionDate: order.orderDateTimeLocal || moment().format(),
        transactionAmount: order.orderTotalAmount,
        onlineOrderId: order.orderId
      };      
      
      await this.$services.retroCredit.submit(retroCreditRequest);
    }  
  }

  onClickExplainRewards(event: any): void {
    if (event.target.className) {
      this.$services.ui.setLastSelectedElement(event.target.className);
    }
    this.$services.ui.setModalVisibility('loyaltyExplainer', true, {
      state: LoyaltyExplainerModalState.Explainer,
    });
    this.$services.ui.firstFocus();
  }

  setCurrentCountry(country: string): void {
    this.currentCountry = country;

    this.showForm = false;
    this.$nextTick(() => {
      this.showForm = true;
    });
  }
}
